import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Copy, RefreshCw, Check, CalendarPlus, Wand2, ArrowLeft, ChevronRight, Megaphone, HeartHandshake, Target, Pencil, Save, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { DraftActions } from "@/components/DraftActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STAGES = [
  { key: "tofu", label: "Top of Funnel", name: "Awareness", desc: "Tarik perhatian audiens baru", color: "#DA6C46", icon: Megaphone },
  { key: "mofu", label: "Middle of Funnel", name: "Trust", desc: "Bangun kepercayaan & kedekatan", color: "#B08968", icon: HeartHandshake },
  { key: "bofu", label: "Bottom of Funnel", name: "Conversion", desc: "Dorong aksi & penjualan", color: "#7C8A57", icon: Target },
];

const STEPS = [
  { key: "hook", label: "Hook", desc: "Pembuka yang bikin berhenti scroll" },
  { key: "foreshadow", label: "Foreshadow", desc: "Teaser yang bikin penasaran" },
  { key: "body", label: "Story (Isi Konten)", desc: "Kerangka inti yang mengalir" },
  { key: "cta", label: "Payoff (Call to Action)", desc: "Ajakan penutup yang natural" },
];

function buildDraft(topic, sel) {
  const parts = [];
  if (topic) parts.push(`# ${topic}`);
  STEPS.forEach((s) => { if (sel[s.key]) parts.push(`${s.label.toUpperCase()}\n${sel[s.key]}`); });
  return parts.join("\n\n");
}

function MiniComposer({ idea, identityId, stageColor, onBack, onSendToCalendar }) {
  const topic = idea.angle ? `${idea.title} — ${idea.angle}` : idea.title;
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState({});
  const [loadingStep, setLoadingStep] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const savedSig = useRef("");

  const draft = buildDraft(topic, selected);
  const shown = deleted ? "" : (edited != null ? edited : draft);
  const complete = STEPS.every((s) => selected[s.key]);

  const saveToLibrary = async (text) => {
    try { await api.post("/scripts", { topic, ...selected, full_text: text }); return true; }
    catch (e) { return false; }
  };
  const copySave = async () => {
    try { await navigator.clipboard.writeText(shown); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
    const ok = await saveToLibrary(shown);
    toast.success(ok ? "Disalin & disimpan ke Library Skrip" : "Disalin ke clipboard");
  };
  const finishEdit = async () => {
    setEditing(false);
    const ok = await saveToLibrary(shown);
    toast.success(ok ? "Perubahan disimpan sebagai versi terbaru" : "Perubahan disimpan");
  };
  const deleteDraft = () => {
    if (!window.confirm("Hapus draft ini? Tindakan ini tidak bisa dibatalkan.")) return;
    setDeleted(true); setEditing(false);
    toast.success("Draft dihapus");
  };

  useEffect(() => {
    if (complete && draft && savedSig.current !== draft) {
      savedSig.current = draft;
      api.post("/scripts", { topic, ...selected, full_text: draft })
        .then(() => toast.success("Draft otomatis tersimpan ke Library Skrip"))
        .catch(() => {});
    }
  }, [complete, draft]); // eslint-disable-line

  const generate = async (step) => {
    setDeleted(false); setEdited(null); setEditing(false);
    setLoadingStep(step);
    try {
      const res = await api.post("/script/generate", { component: step, topic, identity_id: identityId || null, selected });
      setOptions((o) => ({ ...o, [step]: res.data.options }));
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
    } finally {
      setLoadingStep(null);
    }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); setCopied(true); toast.success("Draft disalin ke clipboard"); setTimeout(() => setCopied(false), 1800); }
    catch (e) { toast.error("Gagal menyalin — coba salin manual ya"); }
  };

  return (
    <div>
      <button onClick={onBack} data-testid="mini-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-sk-muted hover:text-sk-cream mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Balik ke ide
      </button>

      <div className="rounded-2xl border border-sk-line bg-sk-surface p-5 shadow-sm mb-6" style={{ borderLeftWidth: 4, borderLeftColor: stageColor }}>
        <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: stageColor }}>Ide terpilih</p>
        <h2 className="font-extrabold text-lg text-sk-cream tracking-tight">{idea.title}</h2>
        {idea.angle && <p className="text-sm text-sk-muted mt-1 leading-relaxed">{idea.angle}</p>}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-5">
          {STEPS.map((step, idx) => (
            <div key={step.key} className={`rounded-2xl border bg-sk-surface p-6 shadow-sm ${loadingStep === step.key ? "sk-generating" : "border-sk-line"}`}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-3">
                  <span className="h-7 w-7 rounded-full bg-sk-brand/12 text-sk-brand font-bold text-sm flex items-center justify-center shrink-0">{idx + 1}</span>
                  <div>
                    <h3 className="font-bold text-sk-cream">{step.label}</h3>
                    <p className="text-xs text-sk-muted">{step.desc}</p>
                  </div>
                </div>
                <button onClick={() => generate(step.key)} disabled={loadingStep === step.key} data-testid={`mini-generate-${step.key}-btn`} className="flex items-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold text-sm px-4 py-2 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sk-brandDark disabled:opacity-60 shrink-0">
                  {loadingStep === step.key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {options[step.key] ? "Ulangi" : "Generate"}
                </button>
              </div>
              <AnimatePresence>
                {options[step.key] && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="grid gap-2.5 mt-4">
                    {options[step.key].map((opt, i) => {
                      const isSel = selected[step.key] === opt;
                      return (
                        <button key={i} data-testid={`mini-option-${step.key}-${i}`} onClick={() => setSelected((s) => ({ ...s, [step.key]: opt }))} className={`text-left rounded-xl border p-3.5 text-sm leading-relaxed transition-colors ${isSel ? "border-sk-brand bg-sk-brand/10 text-sk-cream" : "border-sk-line bg-sk-bg text-sk-muted hover:border-sk-surface2 hover:text-sk-cream"}`}>
                          <div className="flex items-start gap-2.5">
                            <span className={`mt-0.5 h-4 w-4 rounded-full border shrink-0 flex items-center justify-center ${isSel ? "border-sk-brand bg-sk-brand" : "border-sk-muted"}`}>{isSel && <Check className="h-3 w-3 text-sk-ink" />}</span>
                            <span>{opt}</span>
                          </div>
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm lg:sticky lg:top-6">
            <h3 className="font-extrabold text-lg text-sk-cream tracking-tight mb-1">Draft Skrip</h3>
            <p className="text-xs text-sk-muted mb-4">Opsi yang kamu pilih otomatis kegabung di sini.</p>
            {shown ? (
              <>
                {editing ? (
                  <textarea data-testid="mini-draft-edit" value={shown} onChange={(e) => setEdited(e.target.value)} rows={12} className="w-full rounded-xl bg-sk-bg border border-sk-brand p-4 text-sm text-sk-cream resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand leading-relaxed" />
                ) : (
                  <div data-testid="mini-draft-preview" className="rounded-xl bg-sk-bg border border-sk-line p-4 text-sm text-sk-cream whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto">{shown}</div>
                )}
                <DraftActions
                  className="mt-4"
                  editing={editing}
                  onToggleEdit={() => (editing ? finishEdit() : setEditing(true))}
                  onCopy={copySave}
                  copied={copied}
                  onDelete={deleteDraft}
                  editTestid="mini-edit-btn"
                  copyTestid="mini-copy-draft-btn"
                  deleteTestid="mini-delete-btn"
                />
                <button onClick={() => onSendToCalendar(shown)} data-testid="mini-send-to-calendar-btn" className="w-full mt-2.5 flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-2.5 text-sm hover:bg-sk-brandDark transition-colors">
                  <CalendarPlus className="h-4 w-4" /> Kirim ke Kalender
                </button>
                {complete && !deleted && <p className="text-xs text-sk-muted mt-3 flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-sk-brand" /> Draft lengkap & tersimpan otomatis di Library</p>}
              </>
            ) : (
              <div className="text-center py-10 px-2">
                <ChevronRight className="h-8 w-8 text-sk-brand/50 mx-auto mb-3" />
                <p className="text-sk-muted text-sm leading-relaxed">Generate & pilih opsi buat tiap bagian. Draft skrip kamu bakal muncul di sini.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PetaKonversi({ onSendToCalendar }) {
  const [profiles, setProfiles] = useState([]);
  const [identityId, setIdentityId] = useState("");
  const [stage, setStage] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);

  useEffect(() => {
    api.get("/identities").then((r) => { setProfiles(r.data); if (r.data.length > 0) setIdentityId(r.data[0].id); }).catch(() => {});
  }, []);

  const pickStage = async (s) => {
    setStage(s.key);
    setSelectedIdea(null);
    setIdeas([]);
    setLoading(true);
    try {
      const res = await api.post("/funnel/ideas", { stage: s.key, identity_id: identityId || null });
      setIdeas(res.data.ideas);
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
    } finally {
      setLoading(false);
    }
  };

  const activeStage = STAGES.find((s) => s.key === stage);

  if (selectedIdea) {
    return (
      <div>
        <div className="mb-6">
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Peta Konversi · {activeStage?.label}</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Susun Script</h1>
        </div>
        <MiniComposer idea={selectedIdea} identityId={identityId} stageColor={activeStage?.color} onBack={() => setSelectedIdea(null)} onSendToCalendar={onSendToCalendar} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Strategi Funnel</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Peta Konversi</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Pilih tahap funnel, AI kasih ide konten yang pas buat tiap tahap sesuai brand kamu. Klik ide buat langsung susun skripnya.</p>
        </div>
        {profiles.length > 0 && (
          <Select value={identityId} onValueChange={setIdentityId}>
            <SelectTrigger className="w-[180px] bg-sk-surface border-sk-line rounded-xl text-sk-cream" data-testid="funnel-dna-select">
              <SelectValue placeholder="Pilih DNA brand" />
            </SelectTrigger>
            <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
              {profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`funnel-dna-opt-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Stage buttons */}
      <div className="grid sm:grid-cols-3 gap-4">
        {STAGES.map((s) => {
          const isActive = stage === s.key;
          return (
            <button
              key={s.key}
              data-testid={`stage-${s.key}-btn`}
              onClick={() => pickStage(s)}
              className="text-left rounded-2xl border bg-sk-surface p-5 shadow-sm transition-transform duration-200 hover:-translate-y-1"
              style={{ borderColor: isActive ? s.color : "#EADFCE", borderWidth: isActive ? 2 : 1, boxShadow: isActive ? `0 8px 24px -8px ${s.color}66` : undefined }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}1F`, color: s.color }}>
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${s.color}1F`, color: s.color }}>{s.name}</span>
              </div>
              <p className="font-extrabold text-xl tracking-tight" style={{ color: s.color }}>{s.label}</p>
              <p className="text-sm text-sk-muted mt-1 leading-relaxed">{s.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Ideas */}
      <div className="mt-8">
        {loading ? (
          <div className={`rounded-2xl border bg-sk-surface p-10 shadow-sm sk-generating flex flex-col items-center justify-center gap-3`}>
            <div className="h-8 w-8 rounded-full border-2 border-sk-line border-t-sk-brand animate-spin" />
            <p className="text-sk-muted text-sm">Meracik ide {activeStage?.label} yang pas buat brand kamu...</p>
          </div>
        ) : ideas.length > 0 ? (
          <>
            <h2 className="font-extrabold text-lg text-sk-cream tracking-tight mb-4">Ide konten {activeStage?.label} · <span style={{ color: activeStage?.color }}>{activeStage?.name}</span></h2>
            <div className="grid gap-3" data-testid="funnel-ideas">
              {ideas.map((idea, i) => (
                <motion.button
                  key={i}
                  data-testid={`funnel-idea-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setSelectedIdea(idea)}
                  className="text-left rounded-2xl border border-sk-line bg-sk-surface p-5 shadow-sm hover:-translate-y-0.5 transition-transform duration-200 flex items-start justify-between gap-4"
                  style={{ borderLeftWidth: 4, borderLeftColor: activeStage?.color }}
                >
                  <div className="min-w-0">
                    <p className="font-bold text-sk-cream">{idea.title}</p>
                    {idea.angle && <p className="text-sm text-sk-muted mt-1 leading-relaxed">{idea.angle}</p>}
                  </div>
                  <span className="shrink-0 text-xs font-semibold flex items-center gap-1" style={{ color: activeStage?.color }}>Susun <ChevronRight className="h-4 w-4" /></span>
                </motion.button>
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-sk-line bg-sk-surface p-10 text-center shadow-sm" data-testid="funnel-empty">
            <p className="text-sk-cream font-bold">Pilih tahap funnel di atas</p>
            <p className="text-sk-muted text-sm mt-2 max-w-sm mx-auto leading-relaxed">Top of Funnel buat narik audiens baru, Middle of Funnel buat bangun trust, Bottom of Funnel buat closing. AI bakal kasih ide yang pas.</p>
          </div>
        )}
      </div>
    </div>
  );
}
